import { useCallback, useEffect, useRef, useState } from 'react';
import { Balances, TokenGateway } from '../../../application/governance/ports';
import { activateVotes, needsActivation, unwrapZeus, WrapResult, WrapStep, wrapZeus } from '../../../application/governance/wrapUseCases';
import { formatTokens, parseAmount, toInput } from '../../../domain/governance/wrap';
import { Message } from '../ens/useEnsPage';

/** Everything /governance shows and does, for the desktop and the phone layouts alike. */

export type Mode = 'wrap' | 'unwrap';

const STEP_TEXT: Record<WrapStep | 'activating', string> = {
  approving: 'Step 1 of 2: approve exactly this amount in your wallet...',
  wrapping: 'Wrapping your ZEUS, confirm in your wallet...',
  unwrapping: 'Unwrapping your wZEUS, confirm in your wallet...',
  activating: 'Activating your votes, confirm in your wallet...',
};

const failText = (r: Exclude<WrapResult, { ok: true }>) => {
  if (r.reason === 'balance') return 'That is more than you have.';
  if (r.reason === 'rejected') return 'You cancelled it in your wallet. Nothing changed.';
  return 'message' in r ? r.message : 'Something went wrong';
};

export function useGovernancePage({ address, gateway }: { address: string | null; gateway: TokenGateway | null }) {
  const [bal, setBal] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; data: Balances | null }>({ status: 'idle', data: null });
  const [mode, setModeRaw] = useState<Mode>('wrap');
  const [amount, setAmountRaw] = useState('');
  const [step, setStep] = useState<WrapStep | 'activating' | null>(null);
  const [msg, setMsg] = useState<Message>(null);
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  const load = useCallback(async (): Promise<Balances | null> => {
    if (!gateway) {
      setBal({ status: 'idle', data: null });
      return null;
    }
    setBal((b) => ({ ...b, status: 'loading' }));
    try {
      const data = await gateway.read();
      if (alive.current) setBal({ status: 'ready', data });
      return data;
    } catch {
      if (alive.current) setBal((b) => ({ ...b, status: 'error' }));
      return null;
    }
  }, [gateway]);

  useEffect(() => {
    load();
  }, [load]);

  const decimals = bal.data?.decimals ?? 9;
  const parsed = parseAmount(amount, decimals);
  const fmt = (v: bigint) => formatTokens(v, decimals);

  const setMode = (m: Mode) => {
    setModeRaw(m);
    setAmountRaw('');
    setMsg(null);
  };
  const setAmount = (v: string) => {
    setAmountRaw(v.replace(/[^\d.,]/g, ''));
    setMsg(null);
  };
  const setMax = () => {
    if (!bal.data) return;
    setAmountRaw(toInput(mode === 'wrap' ? bal.data.zeus : bal.data.wzeus, decimals));
    setMsg(null);
  };

  const submit = async () => {
    if (!gateway) return;
    if (!parsed) {
      setMsg({ tone: 'bad', text: 'Type an amount above zero.' });
      return;
    }
    const onStep = (s: WrapStep) => {
      setStep(s);
      setMsg({ tone: 'info', text: STEP_TEXT[s] });
    };
    const r = mode === 'wrap' ? await wrapZeus({ amount: parsed, gateway, onStep }) : await unwrapZeus({ amount: parsed, gateway, onStep });
    if (!alive.current) return;
    setStep(null);
    if (!r.ok) {
      setMsg({ tone: 'bad', text: failText(r) });
      return;
    }
    const after = await load();
    if (!alive.current) return;
    setAmountRaw('');
    const done = mode === 'wrap' ? `Wrapped ${fmt(parsed)} ZEUS into wZEUS.` : `Unwrapped ${fmt(parsed)} wZEUS back into ZEUS.`;
    const next = mode === 'wrap' && after && needsActivation(after) ? ' Now activate your votes so they count.' : '';
    setMsg({ tone: 'good', text: done + next });
  };

  const activate = async () => {
    if (!gateway) return;
    setStep('activating');
    setMsg({ tone: 'info', text: STEP_TEXT.activating });
    const r = await activateVotes({ gateway });
    if (!alive.current) return;
    setStep(null);
    if (!r.ok) {
      setMsg({ tone: 'bad', text: failText(r) });
      return;
    }
    const after = await load();
    if (!alive.current) return;
    setMsg({ tone: 'good', text: `Your ${fmt(after ? after.votes : BigInt(0))} votes count now. Go vote on Tally.` });
  };

  const balance = bal.data ? (mode === 'wrap' ? bal.data.zeus : bal.data.wzeus) : BigInt(0);
  const action = parsed ? `${mode === 'wrap' ? 'Wrap' : 'Unwrap'} ${fmt(parsed)} ${mode === 'wrap' ? 'ZEUS' : 'wZEUS'}` : mode === 'wrap' ? 'Wrap ZEUS' : 'Unwrap wZEUS';

  return {
    address,
    connected: !!address,
    bal,
    load,
    mode,
    setMode,
    amount,
    setAmount,
    setMax,
    parsed,
    balance,
    fmt,
    action,
    busy: step !== null,
    step,
    submit,
    msg,
    activate,
    mustActivate: !!bal.data && needsActivation(bal.data),
  };
}

export type GovernancePageState = ReturnType<typeof useGovernancePage>;
